/**
 * RendererSlot — looks up a renderer by tool name in the registry and
 * renders the matched component, or returns null if no renderer is
 * registered for that tool.
 *
 * Use from the chat surface where you'd otherwise have a giant switch
 * statement. The slot is the single point of dispatch for AI-rendered
 * cards.
 *
 * Example:
 *   {actions.map((a, i) => (
 *     <RendererSlot
 *       key={i}
 *       tool={a.tool}
 *       input={a.input}
 *       onConsume={(action) => sendFollowupToAI(action.followupMessage)}
 *     />
 *   ))}
 *
 * Renderers register themselves on import. Make sure the renderer modules
 * are imported somewhere (typically a barrel file) so registration runs
 * before the slot is mounted. See src/framework/react/renderers/index.ts.
 */

import { defaultRegistry, type ConsumeAction } from "./registry.js";

interface RendererSlotProps {
  /** Tool name — e.g. "render_diff_panel", "render_chart". */
  tool: string;
  /** Choreography input passed to the registered component as-is. */
  input: unknown;
  /** Whether this card has been consumed (e.g. plan approved). */
  consumed?: boolean;
  /** Callback the renderer fires when its action is taken. The host
   *  app typically sends followupMessage back to the AI to trigger a
   *  reverse operation. */
  onConsume?: (action: ConsumeAction) => void;
}

export function RendererSlot({ tool, input, consumed, onConsume }: RendererSlotProps) {
  const spec = defaultRegistry.get(tool);
  if (!spec) return null;
  const Component = spec.Component;
  return <Component input={input} consumed={consumed} onConsume={onConsume} />;
}

/**
 * Convenience: returns true when a renderer is registered for the given
 * tool name. Use it before laying out a wrapper element so you don't
 * render an empty card slot.
 */
export function hasRenderer(tool: string): boolean {
  return defaultRegistry.has(tool);
}
